import { Formik } from "formik";
import find from "lodash/find";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import pick from "lodash/pick";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  completePreassignment as employerCompletePreassignment,
  editPrerequisites as employerEditPrerequisites,
} from "../../../../actions/employers/contractActions";
import {
  completePreassignment as recruiterCompletePreassignment,
  editPrerequisites as recruiterEditPrerequisites,
} from "../../../../actions/recruiters/contractActions";

import { clearModal } from "../../../../actions/shared/uiActions";
import { getCurrentIndividual } from "../../../../selectors/individualSelectors";
import { getTempPrerequisites } from "../../../../selectors/settingSelectors";
import { buildFullName } from "../../../../util/formatHelpers";
import { getFileValueAttributes } from "../../../../util/storageHelpers";
import { object, string, array } from "../../../../util/yup";

import useSnackbar from "../../../General/customHooks/useSnackbar";

import {
  determineChecklistItemType,
  SOW_FIELD,
  BASIC_CONFIRMATION_FIELD,
  DATE_FIELD,
  DOCUMENT_FIELD,
  allChecklistItemsComplete,
  checkResponsibility,
  CONTRACT_SUPPLIER_INDIVIDUALS_FIELD,
  WFM_PROFILE_ID_FIELD,
  PEOPLENET_ID_FIELD,
} from "./fieldHelpers";

function buildIntialValues(candidate, contract) {
  const { tempPrerequisites } = candidate.attributes;

  // order handled here, by both field type and id to ensure consistent ordering of array items
  // between form re-initializations on candidate update
  const orderedPrerequisites = orderBy(
    tempPrerequisites,
    [
      (preReq) =>
        determineChecklistItemType(preReq.attributes) === SOW_FIELD ? -1 : 1,
      (preReq) =>
        determineChecklistItemType(preReq.attributes) ===
        BASIC_CONFIRMATION_FIELD
          ? -1
          : 1,
      (preReq) =>
        determineChecklistItemType(preReq.attributes) === DOCUMENT_FIELD
          ? -1
          : 1,
      (preReq) =>
        determineChecklistItemType(preReq.attributes) === DATE_FIELD ? -1 : 1,
      (preReq) =>
        determineChecklistItemType(preReq.attributes) ===
        CONTRACT_SUPPLIER_INDIVIDUALS_FIELD
          ? -1
          : 1,
      (preReq) =>
        determineChecklistItemType(preReq.attributes) === WFM_PROFILE_ID_FIELD
          ? -1
          : 1,
      (preReq) =>
        determineChecklistItemType(preReq.attributes) === PEOPLENET_ID_FIELD
          ? -1
          : 1,
      ({ attributes }) => attributes.id,
    ],
    ["asc", "asc", "asc", "asc", "asc", "asc"]
  );

  const contractSupplierIndividuals = (
    contract?.attributes?.supplierIndividuals ?? []
  ).map(({ id, firstName, lastName }) => ({
    value: id,
    label: buildFullName(firstName, lastName),
  }));

  const wfmProfileId = contract?.attributes?.ukgPersonNumber;
  const peoplenetId = contract?.attributes?.peoplenetId;

  const checklistItems = map(orderedPrerequisites, ({ attributes }) => ({
    ...pick(attributes, [
      "id",
      "tempPrerequisiteId",
      "name",
      "description",
      "documentRequired",
      "requestedDate",
      "contractSupplierIndividualsRequired",
      "submittedDate",
      "employerConfirmedAt",
      "employerConfirmerName",
      "employerConfirmationRequired",
      "agencyConfirmedAt",
      "agencyConfirmerName",
      "agencyConfirmationRequired",
      "responsibility",
      "textValue",
      "textInputRequired",
      "prerequisiteType",
    ]),
    document: attributes.document?.url
      ? {
          name: attributes.document.url.split("?")[0].split("/").slice(-1)[0],
          preview: attributes.document.url,
        }
      : null,
    basicConfirmed: checkResponsibility(attributes),
    contractId: candidate.attributes.contractId,
    contractSupplierIndividuals: attributes.contractSupplierIndividualsRequired
      ? contractSupplierIndividuals
      : [],
    wfmProfileId: attributes.wfmProfileIdRequired ? wfmProfileId : "",
    peoplenetId: attributes.peoplenetIdRequired ? peoplenetId : "",
  }));

  return { checklistItems };
}

function buildValidationSchema() {
  // these validations are simply type checks, validations mostly handled manually
  // since this form can be completed in pieces and submitted in an 'incomplete' state
  return object().shape({
    checklistItems: array().of(
      object().shape({
        document: string().nullable(),
        submittedDate: string().nullable(),
        contractSupplierIndividuals: array().nullable(),
        wfmProfileId: string().nullable(),
        peoplenetId: string().nullable(),
        textValue: string().nullable(),
      })
    ),
  });
}

function FormInitializer({ candidate, children, isEditMode }) {
  const dispatch = useDispatch();
  const currentIndividual = useSelector(getCurrentIndividual);
  const contract = useSelector(
    (state) => state.contracts.items[candidate.attributes.contractId]
  );
  const allTempPrerequisites = useSelector(getTempPrerequisites);
  const snackbar = useSnackbar();

  const initialValues = useMemo(
    () => buildIntialValues(candidate, contract),
    [candidate, contract]
  );

  const validationSchema = useMemo(
    () => buildValidationSchema(candidate),
    [candidate]
  );

  const handleSubmit = useMemo(
    () =>
      async (values, { setSubmitting, setStatus }) => {
        const { checklistItems } = values;

        const submitValues = {
          contract_id: candidate.attributes.contractId,
          temp_recruiter_submission_prerequisites: await Promise.all(
            map(checklistItems, async (item) => {
              const preReqItem = find(
                candidate.attributes.tempPrerequisites,
                (preReq) => preReq.attributes.id === item.id
              );

              // we only pass the document up if it's been modified, so we
              // also need to separately track if the document has been deleted (delete_document param)
              const documentAttrs =
                item.document && item.document.lastModified
                  ? await getFileValueAttributes(item.document, "document")
                  : {};

              const prevDocSet = preReqItem.attributes.document?.url;

              const itemValues = {
                id: item.id,
                submitted_date: item.submittedDate,
                confirmation_selected: item.basicConfirmed,
                contract_supplier_individual_ids:
                  item.contractSupplierIndividuals.map(({ value }) => value),
                wfm_profile_id: item.wfmProfileId,
                peoplenet_id: item.peoplenetId,
                text_value: item.textValue,
                delete_document: prevDocSet && !item.document,
                ...documentAttrs,
              };

              return itemValues;
            })
          ),
        };

        const submissionFunc = currentIndividual.employer
          ? employerCompletePreassignment
          : recruiterCompletePreassignment;

        return dispatch(submissionFunc(submitValues))
          .then(() => {
            setSubmitting(false);

            dispatch(clearModal());

            const successMessage = allChecklistItemsComplete(
              checklistItems,
              currentIndividual
            )
              ? "All pre-assignment steps have been completed and the contract is now open."
              : "Pre-Assignment steps have been updated";

            snackbar.showMessage(successMessage);
          })
          .catch((error) => {
            const { errors } = error.response.data;

            if (errors?.wfm_profile_id) {
              setStatus({
                submitErrors: { wfm_profile_id: errors.wfm_profile_id },
              });
            }

            setSubmitting(false);
          });
      },
    [candidate, currentIndividual, dispatch, snackbar]
  );

  return (
    <Formik
      {...{
        initialValues,
        validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true,
        status: { submitErrors: {} },
      }}
    >
      {(formikBag) =>
        children({
          ...formikBag,
          initialValues,
          handleEditModeSubmit: async () => {
            const { values, setSubmitting } = formikBag;

            const tempPrereqIds = values.checklistItems
              .map((item) => item.tempPrerequisiteId)
              .filter(Boolean);

            const submitValues = {
              contract_id: candidate.attributes.contractId,
              temp_prerequisite_ids: tempPrereqIds,
            };

            const editFunc = currentIndividual.employer
              ? employerEditPrerequisites
              : recruiterEditPrerequisites;

            setSubmitting(true);
            return dispatch(editFunc(submitValues))
              .then((res) => {
                setSubmitting(false);

                const successMessage = res.wasActivated
                  ? "All pre-assignment steps have been completed and the contract is now open."
                  : "Pre-assignment steps have been updated";

                snackbar.showMessage(successMessage);
              })
              .catch((error) => {
                setSubmitting(false);
                throw error;
              });
          },
        })
      }
    </Formik>
  );
}

export default FormInitializer;
